// Package imports:
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
import './OrderPage.scss';
// Component imports:
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TradesUntil from '../sigti/TradesUntil';
import OrderSummary from './OrderSummary';
import OrderEventHistory from './OrderEventHistory';
import OrderBookSnapshot from './OrderBookSnapshot';
import MatchedTrades from './MatchedTrades';
import RelatedAlerts from './RelatedAlerts';
// Service imports:
import { turnRequestedToData } from '../../services/utils';
import { sortTickDataWithBook, sortTickData } from '../../services/sortUtils';
import { isUserLoggedIn } from '../../services/loginUtils';
// Action imports:
import { requestPublicOrders, requestPublicTrades, requestLastPrice, requestPublicOpenClosingPrice } from '../../redux-store/actions/publicActions';
import { requestPrivateOrdersAndTrades } from '../../redux-store/actions/privateActions';
import { requestAlerts } from '../../redux-store/actions/alertActions';
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { PublicOrderTickData, PublicTradeTickData } from '../../types/api/PublicTypes';
import { WithBook, BY_LEVEL_SYMBOLS } from '../../types/api/Types';
import { isPrivateDataError } from '../../types/api/PrivateTypes';
import { AlertTickData } from '../../types/api/AlertTypes';
import { OpenClosingPrice } from '../../types/api/OpenClosingPrice';

interface IOwnProps {
    symbol: string;
    orderId: string;
    date: string;
}
type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps & IOwnProps;

const OrderPage: React.FC<Props> = ({
    symbol,
    orderId,
    date,
    selectedTick,
    accessTokenV2,
    hasPrivateData,
    privateOrderAndTradeDoubleMap,
    publicOrderDoubleMap,
    publicTradeDoubleMap,
    lastPriceDoubleMap,
    publicOpenClosingPrice,
    alertsByDate,
    autoComplete,
    requestPublicOrders,
    requestPublicTrades,
    requestLastPrice,
    requestPublicOpenClosingPrice,
    requestAlerts,
    requestPrivateOrdersAndTrades,
    setSelectedTick
}) => {
    const isSymbolByLevel = useMemo(() => BY_LEVEL_SYMBOLS.includes(symbol), [symbol]);
    const [securityType, setSecurityType] = useState<'share' | 'bond' | 'derivative' | 'etf' | null>(null);

    useEffect(() => {
        document.title = `Order ${orderId} - ${symbol} | KODIAK Sigti`;
    }, [symbol, orderId]);

    // Reset selected tick when navigating to a (different) order page.
    useLayoutEffect(() => {
        setSelectedTick(null);
    }, [symbol, orderId, date, setSelectedTick]);

    // Security Type Effect.
    useEffect(() => {
        if (
            securityType === null
            && autoComplete !== undefined
            && autoComplete !== 'waiting'
            && !(autoComplete instanceof Error)
        ) {
            if ('bonds' in autoComplete) {
                const { bonds, shares, derivatives, etf } = autoComplete;
                if (bonds.find(bond => bond.symbol === symbol)) setSecurityType('bond');
                else if (shares.find(share => share.symbol === symbol)) setSecurityType('share');
                else if (derivatives.find(der => der.symbol === symbol)) setSecurityType('derivative');
                else if (etf.find(fund => fund.symbol === symbol)) setSecurityType('etf');
            }
        }
    }, [securityType, autoComplete, symbol]);

    /* Fetch Data Effects */
    // Public Orders.
    useEffect(() => {
        if (!isSymbolByLevel && publicOrderDoubleMap?.[symbol]?.[date] === undefined) {
            requestPublicOrders(symbol, date, accessTokenV2);
        }
    }, [publicOrderDoubleMap, symbol, date, accessTokenV2, isSymbolByLevel, requestPublicOrders]);

    // Public Trades.
    useEffect(() => {
        if (publicTradeDoubleMap?.[symbol]?.[date] === undefined) {
            requestPublicTrades(symbol, date, accessTokenV2);
        }
    }, [publicTradeDoubleMap, symbol, date, accessTokenV2, requestPublicTrades]);

    // Last Price.
    useEffect(() => {
        if (lastPriceDoubleMap?.[symbol]?.[date] === undefined) {
            requestLastPrice(symbol, date, accessTokenV2);
        }
    }, [lastPriceDoubleMap, symbol, date, accessTokenV2, requestLastPrice]);

    // Private Orders and Trades.
    useEffect(() => {
        const privateOrderAndTradeInfo = privateOrderAndTradeDoubleMap?.[symbol]?.[date];
        if (hasPrivateData && securityType !== null && accessTokenV2 !== null) {
            if (privateOrderAndTradeInfo === undefined || isPrivateDataError(privateOrderAndTradeInfo)) {
                const gateway = (securityType === 'bond') ? 'fusion' : 'inet';
                requestPrivateOrdersAndTrades(symbol, date, accessTokenV2, gateway);
            }
        }
    }, [privateOrderAndTradeDoubleMap, symbol, date, securityType, accessTokenV2, hasPrivateData, requestPrivateOrdersAndTrades]);

    // Alerts.
    useEffect(() => {
        const alertInfo = alertsByDate[date];
        if (alertInfo === undefined) {
            requestAlerts(accessTokenV2, date, date);
        }
    }, [alertsByDate, date, accessTokenV2, requestAlerts]);

    // Open/Closing Price (OHLC for the day).
    useEffect(() => {
        if (publicOpenClosingPrice?.[symbol]?.[date]?.[date] === undefined) {
            requestPublicOpenClosingPrice(symbol, date, date);
        }
    }, [publicOpenClosingPrice, symbol, date, requestPublicOpenClosingPrice]);

    /* Transform data */

    // All public orders for the day (with book state).
    const { sortedPublicOrderTickData } = useMemo(() => {
        const publicOrdersData = publicOrderDoubleMap?.[symbol]?.[date];
        const publicOrders = turnRequestedToData(publicOrdersData, []);
        const sortedPublicOrderTickData = sortTickDataWithBook(publicOrders);
        return { sortedPublicOrderTickData };
    }, [publicOrderDoubleMap, symbol, date]);

    // All public trades for the day.
    const { sortedPublicTradeTickData } = useMemo(() => {
        const publicTradesData = publicTradeDoubleMap?.[symbol]?.[date];
        const publicTrades = turnRequestedToData(publicTradesData, []);
        const sortedPublicTradeTickData = sortTickData(publicTrades);
        return { sortedPublicTradeTickData };
    }, [publicTradeDoubleMap, symbol, date]);

    // Filter order events for the specific order ID.
    const orderEvents = useMemo(() => {
        return sortedPublicOrderTickData.filter(
            ({ tick }) => tick.order_id !== null && String(tick.order_id) === String(orderId)
        );
    }, [sortedPublicOrderTickData, orderId]);

    // Get the first order event (Insert) for the summary.
    const firstOrderEvent = orderEvents.length > 0 ? orderEvents[0] : null;
    const lastOrderEvent = orderEvents.length > 0 ? orderEvents[orderEvents.length - 1] : null;

    // Private data for this order.
    const { matchingPrivateOrders, matchingPrivateTrades } = useMemo(() => {
        const privateOrderAndTradeData = turnRequestedToData(
            privateOrderAndTradeDoubleMap?.[symbol]?.[date],
            null
        );
        const privateOrders = privateOrderAndTradeData?.privateOrders ?? [];
        const privateTrades = privateOrderAndTradeData?.privateTrades ?? [];

        const matchingPrivateOrders = privateOrders.filter(
            po => po.market_feed_id !== null && String(po.market_feed_id) === String(orderId)
        );
        const matchingPrivateTrades = privateTrades.filter(
            pt => pt.market_feed_id !== null && String(pt.market_feed_id) === String(orderId)
        );
        return { matchingPrivateOrders, matchingPrivateTrades };
    }, [privateOrderAndTradeDoubleMap, symbol, date, orderId]);

    // Related alerts for this order.
    const relatedAlerts = useMemo(() => {
        const alertsData = alertsByDate?.[date];
        const alerts = turnRequestedToData(alertsData, []);
        return alerts.filter(alert =>
            alert.symbol === symbol && (
                (alert.order_id !== null && String(alert.order_id) === String(orderId)) ||
                (alert.trade_id !== null && matchingPrivateTrades.some(pt => String(pt.trade_id) === String(alert.trade_id)))
            )
        );
    }, [alertsByDate, date, symbol, orderId, matchingPrivateTrades]);

    // Matched trades: public trades where buyer/seller matches the order's member.
    const matchedTrades = useMemo(() => {
        const member = firstOrderEvent?.tick.member;
        if (!member) return [];
        return sortedPublicTradeTickData.filter(trade =>
            trade.trade_buyer === member || trade.trade_seller === member
        );
    }, [sortedPublicTradeTickData, firstOrderEvent]);

    // Spread position at insertion: was the order inside, at, or outside spread?
    const spreadPosition = useMemo(() => {
        if (!firstOrderEvent) return null;
        const { tick, book } = firstOrderEvent;
        const orderPrice = tick.price;
        const side = tick.side;
        if (orderPrice === null || book.bookBidPrice === null || book.bookAskPrice === null) {
            return { label: 'Unknown', detail: 'No book data at insertion' };
        }
        const spread = book.bookAskPrice - book.bookBidPrice;
        if (side === 'bid') {
            if (orderPrice > book.bookBidPrice && orderPrice < book.bookAskPrice) {
                return { label: 'Inside Spread', detail: `Order ${orderPrice} inside [${book.bookBidPrice}, ${book.bookAskPrice}]` };
            } else if (orderPrice === book.bookBidPrice) {
                return { label: 'At Best Bid', detail: `Order = Best Bid (${book.bookBidPrice})` };
            } else if (orderPrice >= book.bookAskPrice) {
                return { label: 'Crossing', detail: `Bid ${orderPrice} ≥ Ask ${book.bookAskPrice}` };
            } else {
                return { label: 'Below Best Bid', detail: `Order ${orderPrice} < Best Bid ${book.bookBidPrice}` };
            }
        } else {
            if (orderPrice < book.bookAskPrice && orderPrice > book.bookBidPrice) {
                return { label: 'Inside Spread', detail: `Order ${orderPrice} inside [${book.bookBidPrice}, ${book.bookAskPrice}]` };
            } else if (orderPrice === book.bookAskPrice) {
                return { label: 'At Best Ask', detail: `Order = Best Ask (${book.bookAskPrice})` };
            } else if (orderPrice <= book.bookBidPrice) {
                return { label: 'Crossing', detail: `Ask ${orderPrice} ≤ Bid ${book.bookBidPrice}` };
            } else {
                return { label: 'Above Best Ask', detail: `Order ${orderPrice} > Best Ask ${book.bookAskPrice}` };
            }
        }
    }, [firstOrderEvent]);

    // Time statistics.
    const timeStats = useMemo(() => {
        if (orderEvents.length === 0) return null;
        const firstTs = orderEvents[0].tick.timestamp ? new Date(orderEvents[0].tick.timestamp).getTime() : null;
        const lastTs = orderEvents[orderEvents.length - 1].tick.timestamp ? new Date(orderEvents[orderEvents.length - 1].tick.timestamp).getTime() : null;
        const durationMs = (firstTs !== null && lastTs !== null) ? lastTs - firstTs : null;

        const updateCount = orderEvents.filter(e => e.tick.update_code === 'Update').length;
        const numFills = matchingPrivateTrades.length;

        const formatDuration = (ms: number) => {
            if (ms < 1000) return `${ms}ms`;
            const seconds = Math.floor(ms / 1000);
            if (seconds < 60) return `${seconds}s`;
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
        };

        return {
            durationMs,
            durationFormatted: durationMs !== null ? formatDuration(durationMs) : '—',
            eventCount: orderEvents.length,
            updateCount,
            numFills,
            orderToTradeRatio: numFills > 0 ? (updateCount / numFills).toFixed(1) : '—'
        };
    }, [orderEvents, matchingPrivateTrades]);

    // Price context: day's OHLC.
    const ohlcData = useMemo(() => {
        const ohlcRequested = publicOpenClosingPrice?.[symbol]?.[date]?.[date];
        const ohlcArray = turnRequestedToData(ohlcRequested, []);
        return ohlcArray.length > 0 ? ohlcArray[0] : null;
    }, [publicOpenClosingPrice, symbol, date]);

    // Bond fields from the first order event.
    const bondFields = useMemo(() => {
        if (securityType !== 'bond' || !firstOrderEvent) return null;
        const tick = firstOrderEvent.tick;
        return {
            yield: tick.yield,
            dirtyPrice: tick.dirty_price,
            duration: tick.duration,
            pricePerPoint: tick.price_per_point
        };
    }, [securityType, firstOrderEvent]);

    // Book state before and after the selected event.
    // Default to the first order event (Insert) when nothing is selected yet.
    const effectiveTick = selectedTick ?? (orderEvents.length > 0 ? orderEvents[0].tick : null);

    // Index of the selected event within orderEvents (defaults to 0 = Insert).
    const selectedEventIndex = useMemo(() => {
        if (effectiveTick === null) return 0;
        const idx = orderEvents.findIndex(({ tick }) => tick === effectiveTick);
        return idx !== -1 ? idx : 0;
    }, [orderEvents, effectiveTick]);

    // Helper: find position in sortedPublicOrderTickData by value (not reference).
    const findPositionByValue = (
        targetTick: { order_id: string | number | null; update_code: string | null; timestamp: string | null },
        data: typeof sortedPublicOrderTickData
    ) => {
        return data.findIndex(({ tick }) =>
            tick.order_id !== null
            && targetTick.order_id !== null
            && String(tick.order_id) === String(targetTick.order_id)
            && tick.update_code === targetTick.update_code
            && tick.timestamp === targetTick.timestamp
        );
    };

    const { bookBefore, bookAfter } = useMemo(() => {
        if (orderEvents.length === 0 || sortedPublicOrderTickData.length === 0) {
            return { bookBefore: [] as typeof sortedPublicOrderTickData, bookAfter: [] as typeof sortedPublicOrderTickData };
        }

        // Determine which tick to use: selectedTick if it matches an order event, otherwise default to Insert (first event).
        let targetTick: typeof orderEvents[0]['tick'] | null = null;

        if (selectedTick !== null) {
            // Check if selectedTick belongs to one of this order's events (by value).
            const matchedEvent = orderEvents.find(({ tick }) =>
                tick.order_id !== null
                && 'order_id' in selectedTick
                && selectedTick.order_id !== null
                && String(tick.order_id) === String(selectedTick.order_id)
                && tick.update_code === (selectedTick as any).update_code
                && tick.timestamp === selectedTick.timestamp
            );
            if (matchedEvent) {
                targetTick = matchedEvent.tick;
            }
        }

        // Default to the first order event (Insert) when no selection or no match.
        if (targetTick === null && selectedTick === null) {
            targetTick = orderEvents[0].tick;
        }

        if (targetTick !== null) {
            const position = findPositionByValue(targetTick, sortedPublicOrderTickData);
            if (position !== -1) {
                return {
                    bookBefore: sortedPublicOrderTickData.slice(0, position),
                    bookAfter: sortedPublicOrderTickData.slice(0, position + 1)
                };
            }
        }

        // Timestamp-based fallback for non-order selections (e.g., a trade from TradesUntil).
        if (selectedTick !== null && selectedTick.timestamp) {
            const selectedTimestamp = new Date(selectedTick.timestamp).getTime();
            const cutoffIndex = sortedPublicOrderTickData.findIndex(({ tick }) =>
                tick.timestamp !== null && new Date(tick.timestamp).getTime() > selectedTimestamp
            );
            const idx = cutoffIndex === -1 ? sortedPublicOrderTickData.length : cutoffIndex;
            return {
                bookBefore: sortedPublicOrderTickData.slice(0, Math.max(0, idx - 1)),
                bookAfter: sortedPublicOrderTickData.slice(0, idx)
            };
        }

        return { bookBefore: sortedPublicOrderTickData, bookAfter: sortedPublicOrderTickData };
    }, [sortedPublicOrderTickData, orderEvents, selectedTick]);

    // Auto-select first event on load (useLayoutEffect so Redux updates before paint).
    useLayoutEffect(() => {
        if (orderEvents.length > 0 && selectedTick === null) {
            setSelectedTick(orderEvents[0].tick);
        }
    }, [orderEvents, selectedTick, setSelectedTick]);

    // Loading state
    const isLoading = (
        publicOrderDoubleMap?.[symbol]?.[date] === 'waiting'
        || publicOrderDoubleMap?.[symbol]?.[date] === undefined
        || publicTradeDoubleMap?.[symbol]?.[date] === 'waiting'
        || publicTradeDoubleMap?.[symbol]?.[date] === undefined
    );

    if (isSymbolByLevel) {
        return (
            <Container id='OrderPage' fluid>
                <Row className='OrderPageHeader'>
                    <Col md={12}>
                        <h4>Order Execution Analysis</h4>
                        <p>Before/After order book view is not available for market-by-level symbols ({symbol}).</p>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container id='OrderPage' fluid>
            {isLoading ? (
                <Row className='OrderPageHeader'>
                    <Col md={12}>
                        <h4>Order Execution Analysis</h4>
                        <div className='LoadingMessage'>Loading order data...</div>
                    </Col>
                </Row>
            ) : orderEvents.length === 0 ? (
                <Row className='OrderPageHeader'>
                    <Col md={12}>
                        <div className='NotFoundMessage'>
                            <h5>Order not found</h5>
                            <p>No events found for order ID <strong>{orderId}</strong> on {date} for {symbol}.</p>
                        </div>
                    </Col>
                </Row>
            ) : (
                <>
                    {/* Header / Summary */}
                    <Row className='OrderPageHeader'>
                        <Col md={12}>
                            <OrderSummary
                                orderId={orderId}
                                symbol={symbol}
                                date={date}
                                orderEvents={orderEvents}
                                firstOrderEvent={firstOrderEvent}
                                lastOrderEvent={lastOrderEvent}
                                hasPrivateData={hasPrivateData}
                                spreadPosition={spreadPosition}
                                timeStats={timeStats}
                                ohlcData={ohlcData}
                                bondFields={bondFields}
                                securityType={securityType}
                            />
                        </Col>
                    </Row>

                    {/* Order Event History */}
                    <Row className='OrderEventRow'>
                        <Col md={12}>
                            <OrderEventHistory
                                orderEvents={orderEvents}
                                selectedEventIndex={selectedEventIndex}
                            />
                        </Col>
                    </Row>

                    {/* Book Before & After + Trades Until */}
                    <Row className='BookAndTradesRow'>
                        <Col xl={4} md={6} xs={12} className='BookCol'>
                            <h5 className='SectionTitle'>Book Before</h5>
                            <OrderBookSnapshot
                                sortedPublicOrderTickData={bookBefore}
                                highlightOrderIds={[orderId]}
                            />
                        </Col>
                        <Col xl={4} md={6} xs={12} className='BookCol'>
                            <h5 className='SectionTitle'>Book After</h5>
                            <OrderBookSnapshot
                                sortedPublicOrderTickData={bookAfter}
                                highlightOrderIds={[orderId]}
                            />
                        </Col>
                        <Col xl={4} md={12} xs={12} className='TradesCol'>
                            <TradesUntil
                                symbol={symbol}
                                sortedPublicTradeTickData={sortedPublicTradeTickData}
                                effectiveTickOverride={effectiveTick}
                            />
                        </Col>
                    </Row>

                    {/* Matched Trades & Related Alerts */}
                    <Row className='AnalyticsRow'>
                        <Col xl={6} md={12} xs={12}>
                            <MatchedTrades
                                matchedTrades={matchedTrades}
                                member={firstOrderEvent?.tick.member ?? null}
                            />
                        </Col>
                        <Col xl={6} md={12} xs={12}>
                            <RelatedAlerts
                                relatedAlerts={relatedAlerts}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </Container>
    );
};

function mapStateToProps({ dashboardReducer, loginReducer, privateReducer, publicReducer, alertReducer }: RootState) {
    const { privateOrderAndTradeDoubleMap } = privateReducer;
    const { publicOrderDoubleMap, publicTradeDoubleMap, lastPriceDoubleMap, publicOpenClosingPrice } = publicReducer;
    const { selectedTick, autoComplete } = dashboardReducer;
    const { accessTokenV2, hasPrivateData } = loginReducer;
    const { alertsByDate } = alertReducer;
    return {
        selectedTick,
        accessTokenV2,
        hasPrivateData,
        privateOrderAndTradeDoubleMap,
        publicOrderDoubleMap,
        publicTradeDoubleMap,
        lastPriceDoubleMap,
        publicOpenClosingPrice,
        alertsByDate,
        autoComplete
    };
}

const connector = connect(mapStateToProps, {
    requestPublicOrders,
    requestPublicTrades,
    requestLastPrice,
    requestPublicOpenClosingPrice,
    requestAlerts,
    requestPrivateOrdersAndTrades,
    setSelectedTick
});
export default connector(OrderPage);
